import React from 'react';
import { SystemConfig } from '../types';
import { calculateBasicMetrics } from '../utils/calculations';
import { useTheme } from '../contexts/ThemeContext';
import { SectionCard, MetricCard } from './Card';
import { ToggleButton, SliderInput } from './Button';
import { useTranslation } from 'react-i18next';

interface SystemConfigTabProps {
  config: SystemConfig;
  onConfigChange: (config: SystemConfig) => void;
  showStatus: (message: string, type?: 'success' | 'warning' | 'error') => void;
}

const SystemConfigTab: React.FC<SystemConfigTabProps> = ({ config, onConfigChange, showStatus }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const metrics = calculateBasicMetrics(config);

  const handleInputChange = (field: keyof SystemConfig, value: number) => {
    onConfigChange({ ...config, [field]: value });
    showStatus(t('systemConfig.updated', { field: t(`systemConfig.fields.${field}`) }), 'success');
  };

  const handleBladeCountChange = (value: string) => {
    onConfigChange({ ...config, bladeCount: parseInt(value) });
    showStatus(t('systemConfig.updated', { field: t('systemConfig.fields.bladeCount') }), 'success');
  };

  return (
    <div className="space-y-6">


      {/* Configuration Section */}
      <SectionCard title={t('systemConfig.basicParameters')} variant="spotlight">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Parameters */}
          <div className="space-y-6">
            <SliderInput
              label={t('systemConfig.targetWeight')}
              value={config.targetWeight}
              onChange={(value) => handleInputChange('targetWeight', value)}
              min={0.5}
              max={5.0}
              step={0.1}
              unit={t('systemConfig.units.kg')}
            />
            
                          <SliderInput
                label={t('systemConfig.otherPower')}
                value={config.otherPower}
                onChange={(value) => handleInputChange('otherPower', value)}
                min={5}
                max={50}
                step={1}
                unit={t('systemConfig.units.w')}
              />
              
              <SliderInput
                label={t('systemConfig.thrustRatio')}
                value={config.thrustRatio}
                onChange={(value) => handleInputChange('thrustRatio', value)}
                min={1.0}
                max={4.0}
                step={0.1}
                unit=""
              />

              <div>
                <label className="block text-sm font-medium mb-3" style={{ color: colors.text }}>
                  {t('systemConfig.bladeCount')}
                </label>
                <ToggleButton
                  options={[
                    { value: '2', label: `2 ${t('systemConfig.units.blades')}` },
                    { value: '3', label: `3 ${t('systemConfig.units.blades')}` },
                  ]}
                  selected={config.bladeCount.toString()}
                  onChange={handleBladeCountChange}
                />
              </div>
          </div>

          {/* Right Column - Quick Stats */}
          <div className="space-y-6">
            <div>
              <h3 
                className="text-sm font-medium mb-4"
                style={{ color: colors.text }}
              >
                                  {t('systemConfig.title')}
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  <div 
                    className="p-3 rounded-md border"
                    style={{ 
                      backgroundColor: colors.surface,
                      borderColor: colors.borderLight 
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: colors.textSecondary }}>
                        {t('systemConfig.minThrust')}
                      </span>
                      <span className="font-medium" style={{ color: colors.text }}>
                        {metrics.minThrust} {t('systemConfig.units.g')}
                      </span>
                    </div>
                  </div>
                <div 
                  className="p-3 rounded-md border"
                  style={{ 
                    backgroundColor: colors.surface,
                    borderColor: colors.borderLight 
                  }}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{ color: colors.textSecondary }}>
                      {t('systemConfig.singleMotorThrust')}
                    </span>
                    <span className="font-medium" style={{ color: colors.text }}>
                      {metrics.singleMotorThrust} {t('systemConfig.units.g')}
                    </span>
                  </div>
                </div>
                <div 
                  className="p-3 rounded-md border"
                  style={{ 
                    backgroundColor: colors.surface,
                    borderColor: colors.borderLight 
                  }}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{ color: colors.textSecondary }}>
                      {t('systemConfig.motorCount')}
                    </span>
                    <span className="font-medium" style={{ color: colors.text }}>
                      {metrics.motorCount} {t('systemConfig.units.pieces')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Configuration Tips */}
            <div 
              className="p-4 rounded-md"
              style={{ 
                backgroundColor: colors.surface,
              }}
            >
              <h4 className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                {t('systemConfig.configSuggestions')}
              </h4>
              <ul className="text-xs space-y-1" style={{ color: colors.textSecondary }}>
                <li>• {t('systemConfig.suggestions.twoBlades')}</li>
                <li>• {t('systemConfig.suggestions.threeBlades')}</li>
                <li>• {t('systemConfig.suggestions.thrustRatio')}</li>
                <li>• {t('systemConfig.suggestions.powerReserve')}</li>
              </ul>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Performance Metrics */}
      <SectionCard title={t('systemConfig.performanceMetrics')} variant="spotlight">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title={t('systemConfig.thrustUtilization')}
            value={Math.round((metrics.minThrust / (config.targetWeight * 1000)) * 100)}
            unit="%"
            description={t('systemConfig.thrustUtilizationDesc')}
            variant="standard"
            trend="neutral"
          />
          
          <MetricCard
            title={t('systemConfig.estimatedEfficiency')}
            value={config.bladeCount === 2 ? 85 : 78}
            unit="%"
            description={t('systemConfig.estimatedEfficiencyDesc')}
            variant="standard"
            trend={config.bladeCount === 2 ? "up" : "neutral"}
            trendValue={config.bladeCount === 2 ? "+7%" : ""}
          />
          
          <MetricCard
            title={t('systemConfig.estimatedPower')}
            value={Math.round(config.otherPower + (metrics.singleMotorThrust * 2 * 0.1))}
            unit="W"
            description={t('systemConfig.estimatedPowerDesc')}
            variant="standard"
            trend="neutral"
          />
          
          <MetricCard
            title={t('systemConfig.configScheme')}
            value={t('systemConfig.coaxialDualRotor')}
            description={t('systemConfig.configSchemeDesc', { blades: config.bladeCount, motors: metrics.motorCount })}
            variant="standard"
            trend="neutral"
          />
        </div>
      </SectionCard>
    </div>
  );
};

export default SystemConfigTab; 